import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BookOpenText, Plus, Trash, Eye, PencilSimple, ArrowLeft, FilePdf, SpinnerGap, MagicWand, Lightbulb, Scales as ScalesIcon, ChatCircleDots, Strategy, Clock, Fire, Knife, Leaf, CheckCircle, ShareNetwork, Link as LinkIcon, Copy, LinkBreak } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { jsPDF } from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MENU_TYPES = [
  { id: "evento", label: "Evento" },
  { id: "restaurante", label: "Restaurante" },
  { id: "buffet", label: "Buffet" },
  { id: "degustacao", label: "Degustação" },
  { id: "catering", label: "Catering" },
  { id: "outro", label: "Outro" },
];

const AI_ACTIONS = [
  { id: "harmonization", label: "Harmonização", icon: MagicWand, desc: "Sugestões de combinações entre pratos e bebidas" },
  { id: "theme", label: "Temas", icon: Lightbulb, desc: "Transformar cardápio em diferentes estilos" },
  { id: "balance", label: "Equilíbrio", icon: ScalesIcon, desc: "Análise de variedade, custo e progressão" },
];

export default function MenuBuilder() {
  const { t } = useLanguage();
  const [menus, setMenus] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | edit | preview
  const [current, setCurrent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [attackPlan, setAttackPlan] = useState(null);
  const [attackLoading, setAttackLoading] = useState(false);
  const [attackNotes, setAttackNotes] = useState("");
  const [checkedTasks, setCheckedTasks] = useState({});
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", menu_type: "evento", theme: "", guest_count: "" });

  const fetchData = useCallback(async () => {
    try {
      const [menusRes, sheetsRes, themesRes] = await Promise.all([
        axios.get(`${API}/menus`),
        axios.get(`${API}/technical-sheets`),
        axios.get(`${API}/menus/ai/themes`),
      ]);
      setMenus(menusRes.data);
      setSheets(sheetsRes.data);
      setThemes(themesRes.data);
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ==================== CREATE ====================
  const handleCreate = async () => {
    if (!newForm.name.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      const theme = themes.find(t => t.id === newForm.theme);
      const sections = theme
        ? theme.sections.map(s => ({ name: s, items: [], notes: null }))
        : [{ name: "Entradas", items: [], notes: null }, { name: "Pratos Principais", items: [], notes: null }, { name: "Sobremesas", items: [], notes: null }];

      const { data } = await axios.post(`${API}/menus`, {
        name: newForm.name,
        menu_type: newForm.menu_type,
        theme: theme?.name || null,
        guest_count: newForm.guest_count ? parseInt(newForm.guest_count) : null,
        sections,
      });
      toast.success("Cardápio criado!");
      setDialogOpen(false);
      setNewForm({ name: "", menu_type: "evento", theme: "", guest_count: "" });
      setCurrent(data);
      setView("edit");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ==================== OPEN EDIT ====================
  const openMenu = async (menuId) => {
    try {
      const { data } = await axios.get(`${API}/menus/${menuId}`);
      setCurrent(data);
      setView("edit");
      if (data.attack_plan) setAttackPlan(data.attack_plan);
      else setAttackPlan(null);
      setShareToken(data.share_token || null);
      setCheckedTasks({});
      setAttackNotes("");
    } catch { toast.error("Erro ao carregar cardápio"); }
  };

  // ==================== SAVE ====================
  const saveMenu = async () => {
    if (!current) return;
    try {
      await axios.put(`${API}/menus/${current.id}`, current);
      toast.success("Cardápio salvo!");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  // ==================== DELETE ====================
  const handleDelete = async (menuId) => {
    if (!window.confirm("Excluir este cardápio?")) return;
    try {
      await axios.delete(`${API}/menus/${menuId}`);
      toast.success("Cardápio excluído");
      if (current?.id === menuId) { setCurrent(null); setView("list"); }
      fetchData();
    } catch { toast.error("Erro ao excluir"); }
  };

  // ==================== SECTION/ITEM MANAGEMENT ====================
  const addSection = () => {
    setCurrent({ ...current, sections: [...current.sections, { name: "Nova Seção", items: [], notes: null }] });
  };

  const removeSection = (idx) => {
    const s = [...current.sections];
    s.splice(idx, 1);
    setCurrent({ ...current, sections: s });
  };

  const updateSection = (idx, field, value) => {
    const s = [...current.sections];
    s[idx] = { ...s[idx], [field]: value };
    setCurrent({ ...current, sections: s });
  };

  const addItem = (sectionIdx, sheetId) => {
    const sheet = sheets.find(s => s.id === sheetId);
    if (!sheet) return;
    const s = [...current.sections];
    s[sectionIdx].items = [...s[sectionIdx].items, {
      sheet_id: sheet.id, sheet_name: sheet.name,
      description: null, price: null, cost: sheet.total_cost || 0, notes: null,
    }];
    setCurrent({ ...current, sections: s });
  };

  const removeItem = (sectionIdx, itemIdx) => {
    const s = [...current.sections];
    s[sectionIdx].items = s[sectionIdx].items.filter((_, i) => i !== itemIdx);
    setCurrent({ ...current, sections: s });
  };

  const updateItem = (sectionIdx, itemIdx, field, value) => {
    const s = [...current.sections];
    s[sectionIdx].items = [...s[sectionIdx].items];
    s[sectionIdx].items[itemIdx] = { ...s[sectionIdx].items[itemIdx], [field]: value };
    setCurrent({ ...current, sections: s });
  };

  // ==================== AI ====================
  const handleAI = async (type) => {
    if (!current?.id) return;
    await saveMenu();
    setAiLoading(true);
    setAiResult(null);
    try {
      const { data } = await axios.post(`${API}/menus/${current.id}/ai-suggest`, { type });
      setAiResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro na IA"); }
    finally { setAiLoading(false); }
  };

  // ==================== ATTACK PLAN ====================
  const generateAttackPlan = async () => {
    if (!current?.id) return;
    const hasSheets = current.sections?.some(s => s.items?.some(i => i.sheet_id));
    if (!hasSheets) { toast.error("Adicione fichas técnicas ao cardápio antes de gerar o plano"); return; }
    await saveMenu();
    setAttackLoading(true);
    try {
      const { data } = await axios.post(`${API}/menus/${current.id}/attack-plan`, { notes: attackNotes });
      if (data.plan) {
        setAttackPlan(data.plan);
        setCheckedTasks({});
        toast.success("Plano de Ataque gerado!");
      } else {
        toast.error("Formato inesperado. Tente novamente.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar plano"); }
    finally { setAttackLoading(false); }
  };

  const toggleTask = (phaseIdx, taskIdx) => {
    const key = `${phaseIdx}-${taskIdx}`;
    setCheckedTasks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const PHASE_ICONS = { clock: Clock, fire: Fire, knife: Knife, leaf: Leaf };
  const PHASE_COLORS = { 1: "border-blue-200 bg-blue-50", 2: "border-orange-200 bg-orange-50", 3: "border-purple-200 bg-purple-50", 4: "border-emerald-200 bg-emerald-50" };

  const exportAttackPlanPDF = () => {
    if (!attackPlan || !current) return;
    const doc = new jsPDF();
    const w = doc.internal.pageSize.getWidth();
    let y = 20;
    doc.setFontSize(18);
    doc.text(`Plano de Ataque: ${current.name}`, w / 2, y, { align: "center" });
    y += 10;
    if (attackPlan.total_estimated_time) {
      doc.setFontSize(10); doc.setTextColor(100);
      doc.text(`Tempo total estimado: ${attackPlan.total_estimated_time}`, w / 2, y, { align: "center" });
      y += 8;
    }
    doc.setDrawColor(200); doc.line(20, y, w - 20, y); y += 8;

    (attackPlan.phases || []).forEach(phase => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(13); doc.setTextColor(40);
      doc.text(`Fase ${phase.phase}: ${phase.title}`, 20, y); y += 7;
      (phase.tasks || []).forEach(task => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setFontSize(10); doc.setTextColor(60);
        const lines = doc.splitTextToSize(`• ${task.task}`, w - 50);
        doc.text(lines, 25, y); y += lines.length * 5;
        doc.setFontSize(8); doc.setTextColor(120);
        doc.text(`${task.time || ""} — ${task.sheet || ""}`, 30, y); y += 4;
        if (task.tip) { doc.setTextColor(180, 130, 0); doc.text(`Dica: ${task.tip}`, 30, y); y += 4; }
        y += 2;
      });
      y += 5;
    });

    if (attackPlan.pro_tips?.length) {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFontSize(11); doc.setTextColor(40);
      doc.text("Dicas Profissionais:", 20, y); y += 6;
      attackPlan.pro_tips.forEach(tip => {
        doc.setFontSize(9); doc.setTextColor(80);
        const tl = doc.splitTextToSize(`• ${tip}`, w - 40);
        doc.text(tl, 25, y); y += tl.length * 4 + 2;
      });
    }
    doc.save(`Plano_Ataque_${current.name.replace(/\s+/g, "_")}.pdf`);
    toast.success("PDF do Plano de Ataque exportado!");
  };

  // ==================== SHARE ====================
  const getShareUrl = (token) => `${window.location.origin}/menu/${token}`;

  const handleShare = async () => {
    if (!current?.id) return;
    await saveMenu();
    setShareLoading(true);
    try {
      const { data } = await axios.post(`${API}/menus/${current.id}/share`);
      setShareToken(data.share_token);
      setShareDialogOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar link"); }
    finally { setShareLoading(false); }
  };

  const handleCopyLink = () => {
    if (!shareToken) return;
    navigator.clipboard.writeText(getShareUrl(shareToken));
    toast.success("Link copiado!");
  };

  const handleRevokeShare = async () => {
    if (!current?.id) return;
    try {
      await axios.delete(`${API}/menus/${current.id}/share`);
      setShareToken(null);
      setShareDialogOpen(false);
      toast.success("Link desativado");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  // ==================== PDF ====================
  const exportPDF = (viewType) => {
    if (!current) return;
    const doc = new jsPDF();
    const w = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(22);
    doc.text(current.name, w / 2, y, { align: "center" });
    y += 8;
    if (current.theme) {
      doc.setFontSize(12);
      doc.setTextColor(120);
      doc.text(current.theme, w / 2, y, { align: "center" });
      y += 6;
    }
    doc.setDrawColor(200);
    doc.line(20, y, w - 20, y);
    y += 10;

    (current.sections || []).forEach(section => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFontSize(14);
      doc.setTextColor(40);
      doc.text(section.name.toUpperCase(), 20, y);
      y += 8;

      (section.items || []).forEach(item => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setTextColor(60);
        const name = item.sheet_name || "—";
        doc.text(`• ${name}`, 25, y);
        if (viewType === "technical" && item.cost) {
          doc.setTextColor(100);
          doc.text(`R$ ${item.cost.toFixed(2)}`, w - 25, y, { align: "right" });
        }
        if (item.price) {
          doc.setTextColor(40);
          doc.text(`R$ ${item.price.toFixed(2)}`, w - 55, y, { align: "right" });
        }
        y += 5;
        if (item.description) {
          doc.setFontSize(9);
          doc.setTextColor(130);
          const desc = doc.splitTextToSize(item.description, w - 60);
          doc.text(desc, 30, y);
          y += desc.length * 4;
        }
        if (viewType === "technical" && item.notes) {
          doc.setFontSize(8);
          doc.setTextColor(180, 120, 0);
          doc.text(`Nota: ${item.notes}`, 30, y);
          y += 4;
        }
        y += 2;
      });
      y += 6;
    });

    if (viewType === "technical" && current.general_notes) {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text("Notas gerais:", 20, y);
      y += 5;
      doc.setFontSize(9);
      const notes = doc.splitTextToSize(current.general_notes, w - 40);
      doc.text(notes, 20, y);
    }

    doc.save(`${current.name.replace(/\s+/g, "_")}_${viewType}.pdf`);
    toast.success("PDF exportado!");
  };

  // ==================== COMPUTED ====================
  const totalCost = current?.sections?.reduce((acc, s) => acc + s.items.reduce((a, i) => a + (i.cost || 0), 0), 0) || 0;
  const totalItems = current?.sections?.reduce((acc, s) => acc + s.items.length, 0) || 0;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  // ==================== LIST VIEW ====================
  if (view === "list") return (
    <div className="space-y-6" data-testid="menu-builder-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">Cardápios & Menus</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">Crie e gerencie cardápios profissionais com assistência IA</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="new-menu-btn">
          <Plus size={16} className="mr-2" /> Novo Cardápio
        </Button>
      </div>

      {menus.length === 0 ? (
        <Card><CardContent className="text-center py-16">
          <BookOpenText size={56} className="mx-auto text-zinc-200 mb-4" />
          <p className="text-zinc-500 font-medium text-lg">Nenhum cardápio criado</p>
          <p className="text-zinc-400 text-sm mt-1">Crie seu primeiro cardápio profissional</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {menus.map(m => (
            <Card key={m.id} className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => openMenu(m.id)} data-testid={`menu-card-${m.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-zinc-900 truncate">{m.name}</h3>
                    <p className="text-xs text-zinc-400 mt-0.5">{MENU_TYPES.find(t => t.id === m.menu_type)?.label || m.menu_type}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-400 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }}>
                    <Trash size={14} />
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500">
                  <span>{m.section_count} seções</span>
                  <span className="text-zinc-300">·</span>
                  <span>{m.total_items} itens</span>
                  {m.total_cost > 0 && <>
                    <span className="text-zinc-300">·</span>
                    <span className="text-emerald-600 font-medium">R$ {m.total_cost.toFixed(2)}</span>
                  </>}
                </div>
                {m.theme && <p className="text-xs text-blue-500 mt-2">{m.theme}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Menu Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Cardápio</DialogTitle>
            <DialogDescription className="sr-only">Criar cardápio</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nome *</Label>
              <Input value={newForm.name} onChange={e => setNewForm({...newForm, name: e.target.value})} className="mt-1.5" placeholder="Ex: Menu Casamento Silva" data-testid="menu-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Tipo</Label>
                <Select value={newForm.menu_type} onValueChange={v => setNewForm({...newForm, menu_type: v})}>
                  <SelectTrigger className="mt-1.5" data-testid="menu-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MENU_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Convidados</Label>
                <Input type="number" value={newForm.guest_count} onChange={e => setNewForm({...newForm, guest_count: e.target.value})} className="mt-1.5" placeholder="50" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Tema (sugestão IA)</Label>
              <Select value={newForm.theme} onValueChange={v => setNewForm({...newForm, theme: v})}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Personalizado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Personalizado</SelectItem>
                  {themes.map(t => <SelectItem key={t.id} value={t.id}>{t.name} — {t.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreate} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="create-menu-btn">Criar Cardápio</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  // ==================== EDIT VIEW ====================
  if (view === "edit" && current) return (
    <div className="space-y-6" data-testid="menu-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { setView("list"); setCurrent(null); setAiResult(null); }}><ArrowLeft size={18} /></Button>
          <div>
            <Input value={current.name} onChange={e => setCurrent({...current, name: e.target.value})} className="text-xl font-bold border-0 p-0 h-auto focus-visible:ring-0 shadow-none" />
            <p className="text-xs text-zinc-400 mt-0.5">{current.theme || MENU_TYPES.find(t => t.id === current.menu_type)?.label} · {totalItems} itens · Custo: R$ {totalCost.toFixed(2)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleShare} disabled={shareLoading} data-testid="share-menu-btn">
            <ShareNetwork size={14} className="mr-1" /> Compartilhar
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportPDF("presentation")}><FilePdf size={14} className="mr-1" /> PDF</Button>
          <Button onClick={saveMenu} className="bg-zinc-950 text-white hover:bg-zinc-800" size="sm" data-testid="save-menu-btn">Salvar</Button>
        </div>
      </div>

      <Tabs defaultValue="editor">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="editor"><PencilSimple size={16} className="mr-2" /> Editor</TabsTrigger>
          <TabsTrigger value="attack"><Strategy size={16} className="mr-2" /> Plano de Ataque</TabsTrigger>
          <TabsTrigger value="ai"><ChatCircleDots size={16} className="mr-2" /> Sous-Chef IA</TabsTrigger>
        </TabsList>

        {/* EDITOR TAB */}
        <TabsContent value="editor" className="mt-4 space-y-4">
          {(current.sections || []).map((section, sIdx) => (
            <Card key={`section-${sIdx}-${section.name}`} className="overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b">
                <Input value={section.name} onChange={e => updateSection(sIdx, "name", e.target.value)} className="font-semibold text-sm border-0 bg-transparent p-0 h-auto focus-visible:ring-0 shadow-none flex-1" />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-400" onClick={() => removeSection(sIdx)}><Trash size={14} /></Button>
              </div>
              <CardContent className="p-4 space-y-2">
                {section.items.map((item, iIdx) => (
                  <div key={item.sheet_id ? `${item.sheet_id}-${iIdx}` : `item-${sIdx}-${iIdx}`} className="flex items-start gap-2 p-2 rounded border border-zinc-100 bg-white text-sm">
                    <div className="flex-1 space-y-1">
                      <p className="font-medium text-zinc-800">{item.sheet_name}</p>
                      <Input placeholder="Descrição para o cliente..." value={item.description || ""} onChange={e => updateItem(sIdx, iIdx, "description", e.target.value)} className="h-7 text-xs border-zinc-100" />
                      <div className="flex gap-2">
                        <Input placeholder="Preço venda" type="number" value={item.price || ""} onChange={e => updateItem(sIdx, iIdx, "price", parseFloat(e.target.value) || null)} className="h-7 text-xs w-28 border-zinc-100" />
                        <Input placeholder="Notas técnicas..." value={item.notes || ""} onChange={e => updateItem(sIdx, iIdx, "notes", e.target.value)} className="h-7 text-xs flex-1 border-zinc-100" />
                      </div>
                      <p className="text-[11px] text-zinc-400">Custo: R$ {(item.cost || 0).toFixed(2)}{item.price ? ` · Margem: ${(((item.price - (item.cost || 0)) / item.price) * 100).toFixed(0)}%` : ""}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-400 flex-shrink-0 mt-1" onClick={() => removeItem(sIdx, iIdx)}><Trash size={12} /></Button>
                  </div>
                ))}

                {/* Add item */}
                <Select onValueChange={v => v !== "none" && addItem(sIdx, v)}>
                  <SelectTrigger className="h-8 text-xs border-dashed" data-testid={`add-item-section-${sIdx}`}><SelectValue placeholder="+ Adicionar item (ficha técnica)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Selecione...</SelectItem>
                    {sheets.map(s => <SelectItem key={s.id} value={s.id}>{s.name} (R$ {(s.total_cost || 0).toFixed(2)})</SelectItem>)}
                  </SelectContent>
                </Select>

                {/* Section notes */}
                <Input placeholder="Notas da seção..." value={section.notes || ""} onChange={e => updateSection(sIdx, "notes", e.target.value)} className="h-7 text-xs border-zinc-100 mt-2" />
              </CardContent>
            </Card>
          ))}

          <Button variant="outline" onClick={addSection} className="w-full border-dashed" data-testid="add-section-btn">
            <Plus size={14} className="mr-2" /> Adicionar Seção
          </Button>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Notas Gerais</Label>
            <textarea value={current.general_notes || ""} onChange={e => setCurrent({...current, general_notes: e.target.value})} className="mt-1.5 w-full h-20 px-3 py-2 border border-zinc-200 rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-zinc-400" placeholder="Observações gerais do cardápio..." />
          </div>
        </TabsContent>

        {/* ATTACK PLAN TAB */}
        <TabsContent value="attack" className="mt-4 space-y-4">
          {!attackPlan && !attackLoading && (
            <Card className="border-zinc-200">
              <CardContent className="p-6 space-y-4">
                <div className="text-center py-4">
                  <Strategy size={48} className="mx-auto text-zinc-200 mb-3" />
                  <p className="text-zinc-700 font-medium">Gere um Plano de Ataque para este cardápio</p>
                  <p className="text-xs text-zinc-400 mt-1">O Sous-Chef IA vai criar um cronograma completo de produção baseado em todas as fichas técnicas do cardápio</p>
                </div>
                <Input placeholder="Observações para o chef (opcional)..." value={attackNotes} onChange={e => setAttackNotes(e.target.value)} className="text-sm" data-testid="attack-notes-input" />
                <Button onClick={generateAttackPlan} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="generate-attack-plan-btn">
                  <Strategy size={16} className="mr-2" /> Gerar Plano de Ataque
                </Button>
              </CardContent>
            </Card>
          )}

          {attackLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <SpinnerGap size={40} className="animate-spin text-zinc-400 mb-4" />
              <p className="text-zinc-600 font-medium">Sous-Chef está montando o plano...</p>
              <p className="text-xs text-zinc-400 mt-1">Analisando ingredientes, tempos e técnicas de preparo</p>
            </div>
          )}

          {attackPlan && !attackLoading && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-zinc-800">Plano de Ataque</p>
                  {attackPlan.total_estimated_time && <p className="text-xs text-zinc-500">Tempo total: {attackPlan.total_estimated_time}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={exportAttackPlanPDF} data-testid="export-attack-pdf"><FilePdf size={14} className="mr-1" /> PDF</Button>
                  <Button variant="outline" size="sm" onClick={() => { setAttackPlan(null); setCheckedTasks({}); }}>Regerar</Button>
                </div>
              </div>

              {(attackPlan.phases || []).map((phase, pIdx) => {
                const PhaseIcon = PHASE_ICONS[phase.icon] || Clock;
                const colorClass = PHASE_COLORS[phase.phase] || "border-zinc-200 bg-zinc-50";
                const tasks = phase.tasks || [];
                if (tasks.length === 0) return null;
                const doneTasks = tasks.filter((_, tIdx) => checkedTasks[`${pIdx}-${tIdx}`]).length;
                return (
                  <Card key={`phase-${phase.phase}-${pIdx}`} className={`overflow-hidden border ${colorClass.split(" ")[0]}`}>
                    <div className={`flex items-center justify-between px-4 py-3 ${colorClass}`}>
                      <div className="flex items-center gap-2">
                        <PhaseIcon size={18} className="text-zinc-600" />
                        <span className="font-semibold text-sm text-zinc-800">Fase {phase.phase}: {phase.title}</span>
                      </div>
                      <span className="text-xs text-zinc-500">{doneTasks}/{tasks.length}</span>
                    </div>
                    <CardContent className="p-0">
                      {tasks.map((task, tIdx) => {
                        const key = `${pIdx}-${tIdx}`;
                        const done = checkedTasks[key];
                        return (
                          <div key={key} className={`flex items-start gap-3 px-4 py-3 border-b border-zinc-100 last:border-0 cursor-pointer hover:bg-zinc-50/50 ${done ? "opacity-50" : ""}`} onClick={() => toggleTask(pIdx, tIdx)}>
                            <div className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border flex items-center justify-center ${done ? "bg-emerald-500 border-emerald-500" : "border-zinc-300"}`}>
                              {done && <CheckCircle size={14} className="text-white" weight="bold" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm text-zinc-800 ${done ? "line-through" : ""}`}>{task.task}</p>
                              <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-400">
                                {task.time && <span className="flex items-center gap-1"><Clock size={10} /> {task.time}</span>}
                                {task.sheet && <span className="bg-zinc-100 px-1.5 py-0.5 rounded">{task.sheet}</span>}
                              </div>
                              {task.tip && <p className="text-[11px] text-amber-600 mt-1">Dica: {task.tip}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })}

              {attackPlan.pro_tips?.length > 0 && (
                <Card className="border-amber-100 bg-amber-50/30">
                  <CardContent className="p-4">
                    <p className="font-semibold text-sm text-amber-800 mb-2">Dicas Profissionais</p>
                    <ul className="space-y-1">
                      {attackPlan.pro_tips.map((tip, i) => (
                        <li key={`tip-${i}-${tip.slice(0, 20)}`} className="text-sm text-amber-700 flex items-start gap-2">
                          <Lightbulb size={14} className="flex-shrink-0 mt-0.5 text-amber-500" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* AI TAB */}
        <TabsContent value="ai" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {AI_ACTIONS.map(action => (
              <Card key={action.id} className="cursor-pointer hover:shadow-md transition-shadow border-zinc-200 hover:border-zinc-400" onClick={() => handleAI(action.id)} data-testid={`ai-${action.id}-btn`}>
                <CardContent className="p-4 text-center">
                  <action.icon size={28} className="mx-auto text-zinc-400 mb-2" />
                  <p className="font-medium text-sm text-zinc-800">{action.label}</p>
                  <p className="text-[11px] text-zinc-400 mt-1">{action.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {aiLoading && (
            <div className="flex items-center justify-center py-8">
              <SpinnerGap size={32} className="animate-spin text-zinc-400 mr-3" />
              <p className="text-zinc-500">Sous-Chef está analisando seu cardápio...</p>
            </div>
          )}

          {aiResult && !aiLoading && (
            <Card className="border-blue-100 bg-blue-50/30">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ChatCircleDots size={20} className="text-blue-600" />
                  <p className="font-semibold text-sm text-blue-800">Sugestão do Sous-Chef IA</p>
                </div>
                <div className="prose prose-sm max-w-none text-zinc-700 whitespace-pre-wrap">{aiResult.content}</div>
              </CardContent>
            </Card>
          )}

          {(current.ai_suggestions || []).length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Sugestões Anteriores</p>
              {current.ai_suggestions.slice(-3).reverse().map(s => (
                <Card key={s.id} className="border-zinc-100">
                  <CardContent className="p-4">
                    <p className="text-[11px] text-zinc-400 mb-2">{new Date(s.created_at).toLocaleString("pt-BR")} — {AI_ACTIONS.find(a => a.id === s.type)?.label || s.type}</p>
                    <p className="text-sm text-zinc-600 whitespace-pre-wrap line-clamp-6">{s.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* SHARE DIALOG */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShareNetwork size={20} /> Compartilhar Cardápio</DialogTitle>
            <DialogDescription className="sr-only">Link público do cardápio</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-zinc-500">
              Qualquer pessoa com este link poderá visualizar o cardápio <strong>sem precisar de login</strong>. Custos e notas técnicas ficam ocultos — apenas o nome, descrição e preço de venda são exibidos.
            </p>
            {shareToken && (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 text-sm text-zinc-700 truncate font-mono">
                  {getShareUrl(shareToken)}
                </div>
                <Button variant="outline" size="icon" onClick={handleCopyLink} data-testid="copy-share-link">
                  <Copy size={16} />
                </Button>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleCopyLink} className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" data-testid="copy-share-btn">
                <Copy size={14} className="mr-2" /> Copiar Link
              </Button>
              <Button variant="outline" onClick={handleRevokeShare} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="revoke-share-btn">
                <LinkBreak size={14} className="mr-2" /> Desativar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  return null;
}
